import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Pipe,
  PipeTransform,
  Signal,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { AppendTestIdPipe } from '../../../../../../pipes/append-test-id/append-test-id.pipe';
import { WebTextStore } from '../../../../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../../../../pipes/filterTexts/filterTexts.pipe';
import { EEntityType, ERelationType } from '@web/dtos';
import { LanguagePipe } from '../../../../../../pipes/language/language.pipe';

@Pipe({
  name: 'isDayBefore',
  standalone: true,
})
class IsDayBefore implements PipeTransform {
  transform(
    day: number,
    date: Date | undefined,
    month: number,
    year: number
  ): boolean {
    if (!date) {
      return false;
    }
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    const chosenDay: Date = new Date(year, month - 1, day);
    return date.getTime() > chosenDay.getTime();
  }
}

@Pipe({
  name: 'isDayAfter',
  standalone: true,
})
class IsDayAfter implements PipeTransform {
  transform(
    day: number,
    date: Date | undefined,
    month: number,
    year: number
  ): boolean {
    if (!date) {
      return false;
    }
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    const chosenDay: Date = new Date(year, month - 1, day);
    return date.getTime() < chosenDay.getTime();
  }
}

@Pipe({
  name: 'IsDayNotInDateList',
  standalone: true,
})
class IsDayNotInDateList implements PipeTransform {
  transform(
    day: number,
    dateList: Date[] | null,
    month: number,
    year: number
  ): boolean {
    if (!dateList) {
      return false;
    }
    dateList.forEach((date: Date) => date.setHours(0));
    dateList.forEach((date: Date) => date.setMinutes(0));
    dateList.forEach((date: Date) => date.setSeconds(0));
    dateList.forEach((date: Date) => date.setMilliseconds(0));
    const chosenDay: Date = new Date(year, month - 1, day);
    return !dateList.some(
      (date: Date) => date.getTime() === chosenDay.getTime()
    );
  }
}

@Pipe({
  name: 'isDayOfDate',
  standalone: true,
})
class IsDayOfPipe implements PipeTransform {
  transform(day: number, date: Date, month: number, year: number): boolean {
    return (
      date.getMonth() === month - 1 &&
      date.getFullYear() === year &&
      day === date.getDate()
    );
  }
}

@Component({
  selector: 'aw-day-picker',
  imports: [
    NgClass,
    IsDayOfPipe,
    AppendTestIdPipe,
    NgStyle,
    FilterTextsPipe,
    LanguagePipe,
    IsDayBefore,
    IsDayAfter,
    IsDayNotInDateList,
  ],
  templateUrl: './day-picker.component.html',
})
export class DayPickerComponent {
  public readonly id: InputSignal<string> = input.required<string>();
  public readonly date: InputSignal<Date> = input<Date>(new Date());
  public readonly shownMonth: InputSignal<number> = input.required();
  public readonly shownYear: InputSignal<number> = input.required();
  public readonly focusColor: InputSignal<string> = input.required();
  public readonly min: InputSignal<Date | undefined> = input();
  public readonly max: InputSignal<Date | undefined> = input();
  public readonly restrictedDateList: InputSignal<Date[] | null> = input<
    Date[] | null
  >(null);

  public readonly onDayChange: OutputEmitterRef<number> = output<number>();

  protected readonly textStore: WebTextStore = inject(WebTextStore);

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  protected readonly today: Date = new Date();

  protected highlightedDays: number[] = [];

  protected readonly daysOfTheMonth: Signal<number[]> = computed(() => {
    return Array(this.daysInMonth(this.shownYear(), this.shownMonth()))
      .fill(0)
      .map((zero: number, index: number) => index + 1);
  });

  protected readonly previousMonthDays: Signal<number[]> = computed(() => {
    const daysInPreviousMonth: number = this.daysInMonth(
      this.shownYear(),
      this.shownMonth() - 1
    );
    const firstDay: Date = new Date(`
      ${this.shownMonth()}.${this.daysOfTheMonth()[0]}.${this.shownYear()}`);
    const prevMonthDays: number[] = [];
    while (prevMonthDays.length < firstDay.getDay()) {
      prevMonthDays.push(
        daysInPreviousMonth - (firstDay.getDay() - prevMonthDays.length - 1)
      );
    }
    return prevMonthDays;
  });

  protected readonly nextMonthDays: Signal<number[]> = computed(() => {
    const lastDay: Date = new Date(`
      ${this.shownMonth()}.${this.daysOfTheMonth()[this.daysOfTheMonth().length - 1]}.${this.shownYear()}`);
    const postMonthDays: number[] = [];
    while (postMonthDays.length < 6 - lastDay.getDay()) {
      postMonthDays.push(postMonthDays.length + 1);
    }
    return postMonthDays;
  });

  protected removeFromHighlightedDays(day: number): void {
    this.highlightedDays = this.highlightedDays.filter(
      (d: number) => d !== day
    );
  }

  private daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
  }
}
